import React, { useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  Notes as NotesIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { Reservation } from "../types/Reservation";
import { UserPermissions } from "../types/interfaces";
import { formatReservaFechas } from "../styles/reservationStyles";
import { formatCurrency } from "../../shared/utils/formatters";
import { formatDateKey } from "../../shared/utils/dateUtils";
import EstadiasPreviasChip from "./EstadiasPreviasChip";

export interface ReservationTableProps {
  reservations: Reservation[];
  onEdit: (reservation: Reservation) => void;
  onDelete: (reservation: Reservation) => void;
  onVoucher: (reservation: Reservation) => void;
  permissions: UserPermissions;
  pagination?: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  searchTerm?: string;
}

const getNoches = (entrada: string, salida: string): number => {
  const entradaDate = new Date(`${entrada}T00:00:00`);
  const salidaDate = new Date(`${salida}T00:00:00`);
  return Math.max(
    0,
    Math.round((salidaDate.getTime() - entradaDate.getTime()) / 86400000)
  );
};

const getTemporalStatus = (entrada: string, salida: string) => {
  const today = formatDateKey(new Date());

  if (entrada === today) {
    return { label: "Entra hoy", color: "info" as const };
  }

  if (salida === today) {
    return { label: "Sale hoy", color: "warning" as const };
  }

  if (entrada > today) {
    return { label: "Próxima", color: "default" as const };
  }

  if (salida < today) {
    return { label: "Finalizada", color: "default" as const };
  }

  return { label: "En curso", color: "success" as const };
};

const ReservationTable: React.FC<ReservationTableProps> = React.memo(
  ({
    reservations,
    onEdit,
    onDelete,
    onVoucher,
    permissions,
    pagination,
    onPageChange,
    onPageSizeChange,
    searchTerm = "",
  }) => {
    const theme = useTheme();

    const handleChangePage = useCallback(
      (event: unknown, newPage: number) => {
        if (onPageChange) {
          onPageChange(newPage + 1);
        }
      },
      [onPageChange]
    );

    const handleChangeRowsPerPage = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onPageSizeChange) {
          onPageSizeChange(parseInt(event.target.value, 10));
        }
      },
      [onPageSizeChange]
    );

    const handleEdit = useCallback(
      (reservation: Reservation) => {
        onEdit(reservation);
      },
      [onEdit]
    );

    const handleDelete = useCallback(
      (reservation: Reservation) => {
        onDelete(reservation);
      },
      [onDelete]
    );

    const handleVoucher = useCallback(
      (reservation: Reservation) => {
        onVoucher(reservation);
      },
      [onVoucher]
    );

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "auto",
            borderRadius: 1,
            borderColor: "divider",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Depto</TableCell>
                <TableCell>Fechas</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Saldo</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reservations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm
                        ? "No se encontraron reservas"
                        : "No hay reservas"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reservations.map((reservation: Reservation) => {
                  const fechas = formatReservaFechas(
                    reservation.entrada,
                    reservation.salida
                  );
                  const zonaColor =
                    parseInt(reservation.departamento) <= 18
                      ? theme.palette.zonas.centro.border
                      : theme.palette.zonas.loma.border;
                  const valorTotal = Number(reservation.valor_total) || 0;
                  const pago = Number(reservation.pago) || 0;
                  const saldo = valorTotal - pago;
                  const noches = getNoches(
                    reservation.entrada,
                    reservation.salida
                  );
                  const origen = reservation.cliente?.origen_reserva;
                  const temporalStatus = getTemporalStatus(
                    reservation.entrada,
                    reservation.salida
                  );
                  const hasNotes = Boolean(reservation.notas?.trim());

                  return (
                    <TableRow key={reservation.id} hover>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={700}>
                            {reservation.nombre} {reservation.apellido}
                          </Typography>
                          {reservation.estadias_previas ? (
                            <EstadiasPreviasChip
                              maxDeptos={2}
                              datos={{
                                cantidad: reservation.estadias_previas.cantidad,
                                departamentos:
                                  reservation.estadias_previas.departamentos,
                                numeroDeEstadia:
                                  reservation.estadias_previas.cantidad + 1,
                                cargando: false,
                              }}
                            />
                          ) : null}
                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                            <Chip
                              label={`#${reservation.id}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                width: "fit-content",
                                color: "text.secondary",
                              }}
                            />
                            {hasNotes && (
                              <Chip
                                icon={<NotesIcon />}
                                label="Tiene notas"
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20,
                                  width: "fit-content",
                                  color: "text.secondary",
                                }}
                              />
                            )}
                          </Stack>
                          {origen && origen !== "directo" && (
                            <Chip
                              label={origen}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                width: "fit-content",
                                textTransform: "capitalize",
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={reservation.departamento}
                          size="small"
                          sx={{
                            bgcolor: alpha(zonaColor, 0.12),
                            color: theme.palette.text.primary,
                            border: "1px solid",
                            borderColor: alpha(zonaColor, 0.28),
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2">
                            {fechas.entrada.fecha} - {fechas.salida.fecha}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">
                              {noches} {noches === 1 ? "noche" : "noches"}
                            </Typography>
                            <Chip
                              label={temporalStatus.label}
                              color={temporalStatus.color}
                              variant="outlined"
                              size="small"
                              sx={{ height: 20 }}
                            />
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>{reservation.dni || "-"}</TableCell>
                      <TableCell>
                        {formatCurrency(valorTotal)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            saldo > 0
                              ? formatCurrency(saldo)
                              : saldo < 0
                              ? `A favor ${formatCurrency(Math.abs(saldo))}`
                              : "Saldada"
                          }
                          color={saldo > 0 ? "warning" : "success"}
                          variant={saldo > 0 ? "filled" : "outlined"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {permissions.canEditReservations && (
                            <Tooltip title="Editar" placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(reservation)}
                                sx={{
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.2
                                    ),
                                  },
                                }}
                              >
                                <EditIcon
                                  sx={{
                                    fontSize: "1.1rem",
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}

                          {permissions.canDeleteReservations && (
                            <Tooltip title="Eliminar" placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(reservation)}
                                sx={{
                                  color: theme.palette.text.secondary,
                                  "&:hover": {
                                    color: theme.palette.error.main,
                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                  },
                                }}
                              >
                                <DeleteIcon
                                  sx={{
                                    fontSize: "1.1rem",
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="Ver comprobante" placement="top">
                            <IconButton
                              size="small"
                              onClick={() => handleVoucher(reservation)}
                              sx={{
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.success.main,
                                    0.2
                                  ),
                                },
                              }}
                            >
                              <ConfirmationNumberIcon
                                sx={{
                                  fontSize: "1.1rem",
                                  color: theme.palette.success.main,
                                }}
                              />
                            </IconButton>
                          </Tooltip>

                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {pagination && (
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                bgcolor: "background.paper",
                zIndex: (theme) => theme.zIndex.appBar,
                boxShadow: "0 -4px 10px rgba(0,0,0,0.12)",
                flexShrink: 0,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                rowsPerPage={pagination.per_page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Registros por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{
                  bgcolor: "transparent",
                }}
              />
            </Box>
          )}
        </TableContainer>
      </Box>
    );
  }
);

export default ReservationTable;
